import { useEffect, useState } from "react";

const META_REGEX = /<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/i;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Detects new frontend deploys for tabs that stay open across releases.
 *
 * Long-lived tabs never re-fetch index.html, so even with a no-cache HTML
 * header the running JS stays on the old build. This hook fetches index.html on
 * tab focus AND on a polling interval (so a continuously-focused tab is also
 * covered), parses the build version from the app-version meta tag, and reports
 * when it differs from the version baked into the running bundle.
 *
 * Returns `{ updateAvailable, firstDetectedAt }`:
 *  - `updateAvailable` — true once a newer build has been seen.
 *  - `firstDetectedAt` — epoch ms of the first detection (null until then), so
 *    the consumer can escalate the prompt after the build has been stale for a
 *    threshold (e.g. a blocking modal after ~24h).
 *
 * It does not reload on its own. `UpdateAvailableNotification` surfaces a prompt
 * and (via useUpdateEnforcer) auto-reloads only at safe boundaries.
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [firstDetectedAt, setFirstDetectedAt] = useState(null);

  useEffect(() => {
    const currentVersion = import.meta.env.VITE_CACHE_VERSION;
    if (!currentVersion) return;

    let cancelled = false;

    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const res = await fetch(`/?v=${Date.now()}`, { cache: "no-cache" });
        if (!res.ok) return;
        const html = await res.text();
        const match = html.match(META_REGEX);
        if (!cancelled && match && match[1] && match[1] !== currentVersion) {
          setUpdateAvailable(true);
          setFirstDetectedAt((prev) => prev ?? Date.now());
        }
      } catch {
        // Offline / network error — ignore and try again next trigger.
      }
    };

    document.addEventListener("visibilitychange", check);
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", check);
      clearInterval(interval);
    };
  }, []);

  return { updateAvailable, firstDetectedAt };
}
